<template>
	<div class="cashdesk-control">
		<div v-if="showStatus" class="gray-50 cashdesk-status" :class="{ stopped: isCashdeskStopped }">
			<p>{{ status }}</p>
		</div>
		<div class="row actions">
			<div class="col cashdesk-actions">
				<div class="cashdesk-info">
					<h3 class="gray-50">Другорядна каса</h3>
					<div v-if="isCashdeskOpenedOrStopped" class="date-and-cashmachine-code">
						<div class="labeled-icon cashdesk-date">
							<i class="icon-start" />
							<span class="gray-50">{{ cashdeskOpenedDate }}</span>
						</div>
						<div v-if="cashMachineCode" class="gray-50 cashmachine-code">РРО {{ cashMachineCode }}</div>
					</div>
				</div>
				<div>
					<div class="buttons">
						<NP-Button
							v-if="isCashdeskClosed"
							type="button"
							class="open-cashdesk-btn"
							:loading="(loadingActions || []).includes('openCashdesk-0')"
							@click.stop="_openCashdesk"
						>
							Відкрити зміну ДК
						</NP-Button>
						<NP-Button
							v-else-if="isCashdeskOpened"
							type="button"
							class="close-cashdesk-btn"
							:loading="(loadingActions || []).includes('closeCashdesk')"
							@click.stop="() => closeCashdesk({ loadingAction: true })"
						>
							Закрити зміну ДК
						</NP-Button>
						<NP-Button
							v-else-if="isCashdeskStopped"
							type="button"
							class="restore-cashdesk-btn"
							:loading="(loadingActions || []).includes('startCashdesk')"
							@click.stop="() => startCashdesk({ loadingAction: true })"
						>
							Відновити зміну ДК
						</NP-Button>
					</div>
				</div>
			</div>
			<div class="col balance-info">
				<div v-if="isCashbookOpened && isCashdeskOpenedOrStopped" class="balance">
					<span class="title">Баланс, готівка</span>
					<div class="amount">
						<span class="cashdesk-balance">
							<span class="uah" />
							{{ cashdeskBalance | formatMoney }}
						</span>
					</div>
				</div>
				<div v-if="isCashdeskOpened" class="money-action-buttons">
					<NP-Button
						type="button"
						class="btn-give-money"
						@click.stop="() => toggleGiveAcceptCashModal({ tab: 'accept_cash' })"
						priority="medium"
					>
						<i class="icon-arrow-down-circle" />
						<span>Внесення</span>
					</NP-Button>
					<NP-Button
						type="button"
						class="btn-take-money"
						@click.stop="() => toggleGiveAcceptCashModal({ tab: 'give_cash' })"
						priority="medium"
					>
						<i class="icon-arrow-down-circle" />
						<span>Видача</span>
					</NP-Button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import BigNumber from 'bignumber.js';
import find from 'lodash/find';
import { DateTime } from 'luxon';
import { mapGetters } from 'vuex';
import capitalize from 'lodash/capitalize';

import isMobileDevice from '@services/is-mobile-device';

export default {
	name: 'CashdeskControlLayout',
	props: {
		root: { type: Object, default: null },
		state: { type: [String, Object], default: 'loading' },
		cashbook: { type: Object, default: null },
		cashdesk: { type: Object, default: null },
		loadingActions: { type: Array, default: () => [] },
		collapsedComponents: { type: Array, default: () => [] },
		openCashdesk: { type: Function, default: null },
		closeCashdesk: { type: Function, default: null },
		toggleSelectCashMachineModal: { type: Function, default: null },
		toggleGiveAcceptCashModal: { type: Function, default: null },
		startCashdesk: { type: Function, default: null }
	},
	filters: {
		capitalize
	},
	computed: {
		...mapGetters({
			isCashdeskClosed: 'root/isCashdeskClosed',
			isCashdeskStopped: 'root/isCashdeskStopped',
			isCashdeskOpened: 'root/isCashdeskOpened',
			isCashbookOpened: 'root/isCashbookOpened'
		}),
		cashMachineCode() {
			return (this.cashdesk && this.cashdesk.cash_machine_code) || '';
		},
		cashdeskOpenedDate() {
			return `${DateTime.fromISO(this.cashdesk.created_at).toFormat('dd/MM/yyyy, HH:mm:ss')}`;
		},
		isCashdeskOpenedOrStopped() {
			return this.isCashdeskOpened || this.isCashdeskStopped;
		},
		cashdeskBalance() {
			return this.cashdesk && new BigNumber(this.cashdesk.in_balance).minus(this.cashdesk.out_balance).toNumber();
		},
		showStatus() {
			return this.isCashdeskStopped;
		},
		status() {
			return find(this.root.lists['cashdesk-statuses'], { id: this.cashdesk && this.cashdesk.status }).text;
		}
	},
	methods: {
		_openCashdesk() {
			if (!isMobileDevice) {
				this.toggleSelectCashMachineModal();
			} else {
				this.openCashdesk({ loadingAction: { id: 0 } });
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@assets/scss/variables.scss';
@import '@assets/scss/mixins.scss';
@import '@assets/scss/shared.scss';

$icon-start: '~@assets/images/icon-start.svg';
$icon-arrow-down-circle: '~@assets/images/arrow-down-circle.svg';

.cashdesk-control {
	width: 100%;
	margin-top: 20px;
	box-sizing: border-box;
	background-color: $white;
	border-radius: $border-radius;
	.icon-start {
		@include make-icon($icon-start, $gray-30, 20px, 20px);
		margin-bottom: 2px;
	}
	.icon-arrow-down-circle {
		@include make-icon($icon-arrow-down-circle, $white, 19px, 19px);
	}
	.uah {
		margin-right: 4px;
	}
	.cashdesk-status {
		font-size: 12px;
		padding: 10px 25px;
		width: 100%;
		& > * {
			margin: 0;
			padding: 0;
		}
		span {
			font-family: Proxima Nova Semibold, sans-serif;
		}
		&.stopped {
			background: $pink;
			color: $red;
		}
		&.closing {
			background: #fcdf9e;
			color: #b68507;
		}
	}
	p {
		margin: 8px 0;
	}
	.row {
		display: flex;
		& .col {
			display: flex;
			flex: 1;
			& > div {
				flex: 1;
			}
		}
	}
	.actions {
		justify-content: space-between;
		padding: 20px 24px;
		.cashdesk-actions {
			max-width: 480px;
			.cashdesk-info {
				flex: auto;
				width: 100%;
			}
			.buttons {
				text-align: right;
			}
			.date-and-cashmachine-code {
				display: flex;
				flex-direction: column;
				flex-wrap: wrap;
				width: 100%;
				& > * {
					margin-top: 5px;
					font-size: 14px;
					display: flex;
					align-items: center;
				}
				.cashmachine-code {
					color: $gray-40;
					margin-left: 25px;
				}
			}
		}
		.balance-info {
			max-width: 450px;
			margin-left: 40px;
			flex-direction: column;
			.money-action-buttons {
				display: flex;
				justify-content: space-between;
				margin-top: 16px;
				& > :not(:last-child) {
					margin-right: 8px;
				}
				::v-deep button {
					width: 100%;
					.text {
						align-items: center;
						justify-content: center;
						display: flex;
						flex-direction: row;
						line-height: 15px;
						i {
							margin-right: 10px;
						}
					}
					&.btn-take-money {
						i {
							transform: rotate(180deg);
						}
					}
				}
			}
		}
		.balance {
			width: 100%;
			align-items: center;
			background-color: $gray-5;
			border-radius: $border-radius;
			display: flex;
			flex-wrap: wrap;
			min-height: 40px;
			max-height: 40px;
			justify-content: space-between;
			padding: 12px;
			font-size: 14px;
			line-height: 17px;
			.title {
				display: flex;
				flex: auto;
			}
			.amount {
				display: flex;
				align-items: center;
				flex-wrap: wrap;
				.cashdesk-balance {
					display: flex;
					align-items: center;
					font-family: Proxima Nova Semibold, sans-serif;
					font-size: 20px;
				}
			}
		}
	}
	@include mobile() {
		.actions {
			width: 100%;
			display: flex;
			flex-direction: column;
			padding: 16px;
			.col {
				width: 100%;
				display: flex;
				flex-direction: column;
				align-items: flex-start;
				margin: 0 0 16px 0;
				.cashdesk-info,
				.balance {
					margin-bottom: 16px;
				}
				.money-action-buttons {
					margin-left: 0;
				}
			}
		}
	}
}
</style>
